// Big Pivot Partners — content per approved Website Strategy, Content & Build Brief.
import { BookOpen, Users, MonitorSmartphone, Sprout } from "lucide-react";

export const CONTACT = {
  headline: "Let’s Talk About What You Need.",
  intro:
    "Tell us what you’re working on — whether it’s early-stage strategy or a report already in motion, we’d welcome the conversation.",
  direct: { name: "Joel Charron", email: "joel@bigpivot.net", phone: "713.294.5974", phoneHref: "tel:+17132945974" },
  partners: [
    { name: "Joel Charron", role: "Partner", email: "joel@bigpivot.net", phone: "713.294.5974", phoneHref: "tel:+17132945974", linkedin: "https://www.linkedin.com/in/joelcharron" },
    { name: "Marc Seligson", role: "Founder & President", email: "marc@bigpivot.net", phone: "954.579.3578", phoneHref: "tel:+19545793578", linkedin: "https://www.linkedin.com/in/marcseligson" },
  ],
};

export const REPORTS = {
  wtOffshore: "/reports/wt-offshore.webp",
  stepan: "/reports/stepan.webp",
  pdfCemex: "/reports/pdf/cemex.webp",
  pdfKodiak: "/reports/pdf/kodiak.webp",
  pdfPenguin: "/reports/pdf/penguin.webp",
  pdfFlexitallic: "/reports/pdf/flexitallic.webp",
  pdfPrimaryEnergy: "/reports/pdf/primary-energy.webp",
  pdfClimeco: "/reports/pdf/climeco.webp",
  pdfBlackberryInvestor: "/reports/pdf/blackberry-investor.webp",
  pdfBlackberryInvestorCover: "/reports/pdf/blackberry-investor-cover.webp",
  pdfBlackberryGrowth: "/reports/pdf/blackberry-growth.webp",
  pdfBlackberryGlance: "/reports/pdf/blackberry-glance.webp",
  pdfAfr: "/reports/pdf/far-afr.webp",
  pdfFarBiotech: "/reports/pdf/int-farbiotech.webp",
  pdfAcc: "/reports/pdf/int-acc.webp",
  pdfKennedyWilsonEsg: "/reports/pdf/int-kennedywilson.webp",
  meritage: "/reports/meritage-homes.webp",
  flexitallic: "/reports/flexitallic.webp",
  penguin: "/reports/penguin-solutions.webp",
  kennedyWilson: "/reports/kennedy-wilson.webp",
  primaryEnergy: "/reports/primary-energy.webp",
  longreach: "/reports/longreach.webp",
  sbp: "/reports/sbp.webp",
  worldCarrier: "/reports/world-carrier.webp",
  performance: "/reports/performance-highlights.webp",
  founders: "/reports/founders.webp",
  blackberryEarnings: "/reports/blackberry-earnings.webp",
  dmiSolutions: "/reports/dmi-solutions.webp",
  dmiInnovaOne: "/reports/dmi-innova-one.webp",
  climecoBrand: "/reports/climeco-brand.webp",
  sb253: "/reports/pdf/sb253-carb-guidance.webp",
  sdgs: "/reports/sdgs-reporting.webp",
  sdgsGoals: "/reports/sdgs-un-goals.webp",
};

export const PORTRAIT_COVERS = new Set([
  REPORTS.wtOffshore,
  REPORTS.stepan,
  REPORTS.kennedyWilson,
  REPORTS.longreach,
  REPORTS.worldCarrier,
  REPORTS.performance,
  REPORTS.founders,
]);

export const landscapeFirst = (items) =>
  [...items].sort((a, b) => {
    const oa = a.order ?? 500, ob = b.order ?? 500;
    if (oa !== ob) return oa - ob;
    const la = a.lead ? 0 : 1, lb = b.lead ? 0 : 1;
    if (la !== lb) return la - lb;
    return (PORTRAIT_COVERS.has(a.image) ? 1 : 0) - (PORTRAIT_COVERS.has(b.image) ? 1 : 0);
  });

export const HERO = {
  eyebrow: "We meet organizations where they are",
  headline: "Trust. Clarity. Credibility.",
  subhead: "We meet you where you are.",
  primaryCta: "View Our Work",
  secondaryCta: "Take the Two-Minute Alignment Diagnostic",
};

export const PROOF = {
  headline: "Experience You Can Trust.",
  points: [
    "Nearly 60 years of combined stakeholder communications experience",
    "Partner-led strategy, creative direction, and project management",
    "Award-winning work across reporting, digital, and corporate communications",
    "Experience supporting organizations from growth companies to the Fortune 500",
  ],
};

export const SERVICES = [
  {
    id: "reporting",
    sample: "/samples/reporting.webp",
    slug: "corporate-reports",
    workAnchor: "corporate-reports",
    icon: BookOpen,
    title: "Reporting & Communications",
    short:
      "Annual and integrated reports, investor and disclosure materials, and other high-stakes corporate reporting designed to make complex information clear, credible, and useful.",
    long:
      "We help organizations transform complex financial, operational, strategic, and performance information into well-structured reports that meet disclosure expectations while telling a clear, credible story. From strategy and content development through design, production, and digital delivery, we bring the rigor and quality expected in enterprise reporting.",
    points: ["Annual & integrated reports", "Proxy & disclosure materials", "Print & digital production"],
  },
  {
    id: "sustainability",
    sample: "/samples/investor.webp",
    slug: "sustainability-strategy-reporting-advisory",
    workAnchor: "sustainability-strategy-advisory",
    icon: Sprout,
    title: "Sustainability Strategy, Reporting & Advisory",
    short:
      "Sustainability strategy, reporting, and advisory that turn complex disclosure into one clear, credible story.",
    long:
      "We help organizations connect sustainability to strategy, performance, and long-term value — from materiality and strategy through reporting, disclosure, and stakeholder engagement. Standards such as IFRS S1/S2, GRI, SASB, the EU’s CSRD/ESRS, and California’s SB 253 and SB 261 provide important structure, but they are not the story. When necessary, we work alongside legal, accounting, sustainability, and assurance partners to ensure compliance and accountability.",
    points: ["Sustainability strategy & materiality", "Sustainability reporting & disclosure", "Framework & disclosure alignment", "Stakeholder communications & advisory"],
  },
  {
    id: "interactive",
    sample: "/samples/interactive.webp",
    slug: "interactive-media",
    workAnchor: "interactive-media",
    icon: MonitorSmartphone,
    title: "Digital Media",
    short:
      "Websites, interactive reports, digital platforms, social media content, and other digital experiences designed to make communications more accessible, engaging, and effective.",
    long:
      "We create digital communications that connect strategy, content, creative, and technology — from corporate websites and interactive reports to digital platforms, social media content, and other digital experiences. Every solution is designed around the audience and the communication objective, making information easier to navigate, understand, engage with, and act on across devices and channels.",
    points: ["Corporate & campaign websites", "Interactive reports & microsites", "Digital platforms & experiences", "Social media content & creative"],
  },
  {
    id: "branding",
    sample: "/samples/corporate.webp",
    slug: "branding-marketing-employee-communication",
    workAnchor: "branding-marketing-employee",
    icon: Users,
    title: "Branding, Marketing & Employee Communication",
    short:
      "Brand systems, marketing campaigns, and employee and executive communications that keep messaging consistent, engaging, and on-brand.",
    long:
      "We help organizations build and maintain a coherent communications system — extending brand and message across campaigns, presentations, employee and executive communications, events, and collateral so every touchpoint reinforces the same clear, credible message.",
    points: ["Brand systems & applications", "Marketing campaigns", "Employee & executive communications", "Presentations & collateral"],
  },
];

export const CORE_SERVICES_INTRO = {
  headline: "Stakeholder Communication Built for Clarity, Credibility, and Action.",
  intro:
    "We bring strategy, content, design, production, and digital execution together — selectively using AI-enabled tools where they add value — to help organizations communicate with the people who matter most.",
};

export const POSITIONING = {
  headline: "One Clear, Credible, Consistent Story.",
  intro:
    "Your company’s story takes shape across reports, presentations, websites, brand materials and employee communications. Big Pivot brings strategic thinking, content and design together to make that story clear, credible and consistent — while giving each audience the information it needs.",
  pillars: [
    { title: "Strategic Messaging", link: "/stakeholder-communications-review", text: "We help clarify what matters, connect it to your business priorities and shape messages that your audiences can understand and act on." },
    { title: "Connected Communications", link: "/stakeholder-communications-review", text: "We align the story across corporate, customer, employee, investor and sustainability materials so each communication reinforces the others." },
    { title: "Purposeful Content + Design", link: "/services#interactive-media", text: "We turn complex information into clear narratives and effective design that make your communications easier to understand and use." },
    { title: "Effective Execution", link: "/services#corporate-reports", text: "We bring communications to life across print and digital — from reports and presentations to websites and brand materials — with senior partners guiding the work." },
  ],
};

export const DISCLOSURE_REVIEW = {
  tag: "Stakeholder Communications Alignment",
  title: "Stakeholder Communications Alignment",
  tagline: "",
  intro:
    "One clear, credible company story across the communications that matter most. We review selected materials to identify where messages connect, where they drift and how content and design can strengthen the overall story.",
  dimensions: ["Corporate + Brand", "Leadership + Employee", "Customer", "Investor", "Annual + Sustainability Reports", "Website + Digital"],
  outputs: [
    { lead: "A clear view of communication gaps", text: "Where messages conflict, claims appear outdated or supporting context is missing." },
    { lead: "Stronger connections across your story", text: "Recommendations to better connect business priorities, performance and stakeholder messaging." },
    { lead: "Opportunities to make your strengths more visible", text: "Valuable accomplishments and differentiators that deserve clearer explanation or greater emphasis." },
    { lead: "A practical plan for improvement", text: "Prioritized recommendations for messaging, content and design across the materials reviewed." },
  ],
};

// Single source of truth for the six Full Review components — mirrored on the diagnostic result
// ("What a Full Review Adds") and the Capability page so the offer reads identically everywhere.
export const REVIEW_COMPONENTS = [
  ["Communications Content + Consistency", "Identify conflicting messages, missing connections and opportunities to make the company story clearer across selected materials."],
  ["Core Narrative + Strategic Messaging", "Clarify the central story and supporting messages, shaped for different audiences."],
  ["Alignment Across Communications", "Assess how corporate, leadership, employee, customer, investor and sustainability materials reinforce the company’s direction."],
  ["Claims + Supporting Context", "Identify where statements, metrics and commitments need clearer explanation, consistent language or further confirmation."],
  ["Content + Communication Design", "Recommend clearer language, stronger information hierarchy and more effective visual presentation."],
  ["Strategic Communication Solutions", "Turn findings into practical recommendations for reports, presentations, websites, brand materials and employee communications."],
];
export const REVIEW_RESULT = "The result is a clearer company story, stronger communications and a focused plan for improvement.";

export const WHY = {
  headline: "Senior experience. Hands-on execution. No unnecessary layers.",
  intro:
    "Complex communications require more than good design. They require judgment, collaboration, disciplined project management, and a clear understanding of the audience, message, and business context.",
  points: [
    { title: "Partner-Led", text: "Senior partners are directly involved in strategy, creative direction, and delivery — not just oversight." },
    { title: "Strategic and Creative", text: "We connect business priorities and stakeholder needs to messaging, structure, and design." },
    { title: "Flexible by Design", text: "We work as an extension of your team, adapting to your process, timeline, and internal resources." },
    { title: "Built to Deliver", text: "Disciplined project management and quality assurance keep high-stakes work on time and on budget." },
  ],
};

export const APPROACH = [
  { step: "01", title: "Clarify", text: "We start by understanding the business context, audience, message, and objectives behind the communication." },
  { step: "02", title: "Shape", text: "We define structure, narrative, and creative direction so the story is clear before production begins." },
  { step: "03", title: "Create", text: "We develop content, design, and digital execution with senior involvement at every stage." },
  { step: "04", title: "Deliver", text: "We manage production, quality assurance, and delivery across print and digital channels." },
];

export const AWARDS = {
  headline: "Award-Winning Reports and Communications",
  copy: "",
  // Award programs Big Pivot’s work has been recognized in
  orgs: [
    {
      org: "Spotlight",
      isSpotlight: true,
      spotlights: [
        { program: "LACP Vision", project: "2025 Integrated Report", honors: ["LACP Platinum", "Most Creative Report (Americas)"], flipbook: "cemex", logo: "/reports/cemex-logo-white.png", link: "https://www.lacp.com/2025vision/awards-annual-report-competition-1180b.htm", linkLabel: "View LACP results" },
        { program: "ARC Awards", project: "2025 Integrated Report", honors: ["ARC Awards", "Silver"], flipbook: "cemex", logo: "/reports/cemex-logo-white.png", link: "https://www.mercommawards.com/arc/awardWinners/categoryWinners/pdf.htm#intEur", linkLabel: "View ARC results" },
      ],
    },
    {
      org: "LACP Vision",
      desc: "League of American Communications Professionals",
      entries: [
        { client: "American Campus Communities", type: "Impact Report" },
        { client: "ChampionX", type: "Sustainability Report" },
        { client: "Dollar General Corporation", type: "Serving Others Report" },
        { client: "eHealth", type: "Sustainability Report" },
        { client: "Essential Utilities", type: "Sustainability Report" },
        { client: "Kennedy Wilson", types: ["Annual Report", "Sustainability Report"] },
      ],
    },
    {
      org: "ARC Awards",
      desc: "International Annual Report Competition",
      entries: [
        { client: "Aflac", type: "Business and Sustainability Report" },
        { client: "CEMEX", type: "Integrated Report" },
        { client: "Maravai LifeSciences", type: "Sustainability Report" },
        { client: "Meritage Homes", type: "Sustainability Report" },
        { client: "SGH (now Penguin Solutions)", type: "Sustainability Report" },
      ],
    },
    {
      org: "IADA",
      desc: "International Annual Report Design Awards",
      entries: [
        { client: "American Campus Communities", type: "Impact Report" },
        { client: "eHealth", type: "Sustainability Report" },
        { client: "Kennedy Wilson", type: "Sustainability Report" },
        { client: "Stepan Company", type: "Annual Report" },
      ],
    },
    {
      org: "GDUSA",
      desc: "American Graphic Design Awards",
      descOneLine: true,
      entries: [
        { client: "Aflac", type: "Sustainability Report" },
        { client: "CEMEX", type: "Integrated Report" },
        { client: "ChampionX", type: "" },
        { client: "ClimeCo", type: "Impact Report" },
        { client: "Dollar General Corporation", type: "Serving Others Report" },
        { client: "Essential Utilities", type: "Sustainability Report" },
        { client: "Flexitallic", type: "Sustainability Report" },
        { client: "ICHOR Systems", type: "Sustainability Report" },
        { client: "Jackson Financial", type: "Annual Report" },
        { client: "The Longreach Group", type: "Impact Report" },
        { client: "OSI Systems", type: "Annual Report" },
        { client: "SGH (now Penguin Solutions)", type: "Annual Report" },
        { client: "Vaalco Energy", type: "Proxy" },
        { client: "W&T Offshore", type: "Annual Report" },
      ],
    },
  ],
  // Individual award entries — edit this list to add/update recognitions.
  // Each item: { year, program, honor, category, client, project, image }
  list: [
    {
      year: "2024",
      program: "LACP Vision Awards",
      honor: "Gold",
      category: "Annual Report",
      client: "Penguin Solutions",
      project: "Annual Report 2024",
      image: REPORTS.penguin,
    },
    {
      year: "2024",
      program: "ARC Awards",
      honor: "Gold",
      category: "Sustainability Report",
      client: "Meritage Homes",
      project: "Life. Built. Better. — 2023 Sustainability Report",
      image: REPORTS.meritage,
    },
    {
      year: "2024",
      program: "GDUSA American Graphic Design Awards",
      honor: "Winner",
      category: "Design Excellence",
      client: "Flexitallic",
      project: "Making the World Safer & Cleaner — 2023 Sustainability",
      image: REPORTS.flexitallic,
    },
    {
      year: "2023",
      program: "IADA",
      honor: "Honors",
      category: "Annual Report Design",
      client: "Kennedy Wilson",
      project: "Annual Report 2023",
      image: REPORTS.kennedyWilson,
    },
    {
      year: "2023",
      program: "ARC Awards",
      honor: "Silver",
      category: "Annual Report",
      client: "W&T Offshore",
      project: "Maintaining Our Focus — 2023 Annual Report",
      image: REPORTS.wtOffshore,
    },
    {
      year: "2023",
      program: "LACP Vision Awards",
      honor: "Gold",
      category: "Sustainability Report",
      client: "Specialty Building Products",
      project: "2023 Corporate Sustainability Report",
      image: REPORTS.sbp,
    },
  ],
};

export const CLOSING = {
  headline: "Making Complex Communications Clearer.",
  copy:
    "Whether you are preparing an annual or integrated report, investor presentation, sustainability report, employee initiative, or digital communications platform, Big Pivot Partners can help you clarify the message, shape the story, and deliver it with confidence.",
  cta: "Start a Conversation",
};

export const CASE_STUDIES = [
  {
    id: "penguin-solutions",
    segment: "Reporting & Communications",
    client: "Penguin Solutions",
    category: "Sustainability Report",
    title: "2025 Environmental & Social Sustainability Report",
    context: "A technology company communicating its environmental and social sustainability strategy and performance to investors and stakeholders.",
    challenge: "Present a complex, transforming business in a clear, confident, and investor-ready report.",
    role: "Reporting strategy, narrative structure, design, and production.",
    solution: "A cohesive report that connects strategy to performance with a clean, modern editorial system.",
    services: ["Reporting & Disclosure", "Corporate Communications"],
    tags: ["Sustainability", "Investor", "Design"],
    image: REPORTS.penguin,
    order: 2,
    pdf: "/reports/pdf/penguin-solutions.pdf",
    flipbook: {
      dir: "/reports/flipbook/penguin-solutions",
      pages: 63,
      ratio: 612 / 1008,
      chapters: [
        { label: "Introduction", page: 5 },
        { label: "Product Innovation", page: 16 },
        { label: "Operational Sustainability", page: 21 },
        { label: "Our Workplace", page: 28 },
        { label: "Community Impact", page: 35 },
        { label: "Supply Chain Responsibility", page: 38 },
        { label: "Governance & Business", page: 41 },
        { label: "Appendix", page: 46 },
      ],
    },
  },
  {
    id: "stepan",
    segment: "Reporting & Communications",
    client: "Stepan Company",
    category: "Annual Report",
    title: "The Chemistry of Transformation — 2025 Annual Report",
    context: "A global specialty chemical manufacturer reporting to shareholders on a year of investment and disciplined, balanced growth.",
    challenge: "Translate complex financial and operational performance into a clear, confident shareholder narrative.",
    role: "Reporting strategy, narrative, design, and production.",
    solution: "An annual report that pairs a strong opening story and financial highlights with a disciplined 10-K presentation.",
    services: ["Reporting & Disclosure"],
    tags: ["Financial", "Annual Report", "Copywriting"],
    image: REPORTS.stepan,
    order: 3,
    uniformCover: true,
    pdf: "/reports/pdf/stepan.pdf",
    flipbook: {
      dir: "/reports/flipbook/stepan",
      pages: 118,
      ratio: 774 / 594,
      chapters: [
        { label: "Financial Highlights", page: 2 },
        { label: "Shareholder Letter", page: 3 },
        { label: "Table of Contents", page: 16 },
        { label: "Business", page: 19 },
        { label: "Risk Factors", page: 23 },
        { label: "Management’s Discussion", page: 39 },
        { label: "Financial Statements", page: 55 },
        { label: "Governance", page: 104 },
      ],
    },
  },
  {
    id: "blackberry-earnings",
    segment: "Reporting & Communications",
    client: "BlackBerry",
    category: "Earnings Presentation",
    title: "Q1 FY27 Earnings Presentation",
    context: "A technology and cybersecurity company presenting quarterly results to investors and analysts.",
    challenge: "Turn quarterly financial and operational data into a clear, confident investor story.",
    role: "Presentation strategy, narrative, and design.",
    solution: "A sharp, on-brand earnings deck that makes the quarter’s performance easy to follow.",
    services: ["Investor & Stakeholder", "Corporate Communications"],
    tags: ["Investor", "Earnings", "Presentation"],
    image: REPORTS.blackberryEarnings,
    order: 5,
    pdf: "/reports/pdf/blackberry-earnings.pdf",
    flipbook: { dir: "/reports/flipbook/blackberry-earnings", pages: 27, ratio: 540 / 960 },
    coverPos: "left",
  },
  {
    id: "vaalco-energy",
    segment: "Reporting & Communications",
    client: "Vaalco Energy",
    category: "Annual Report & Proxy",
    title: "2025 Annual Report & 2026 Proxy Statement",
    context: "An independent energy company communicating annual performance and governance to shareholders.",
    challenge: "Present financial results and governance materials in a clear, credible, investor-ready format.",
    role: "Reporting strategy, narrative, design, and production.",
    solution: "A coordinated annual report and proxy statement with a consistent, confident editorial system.",
    services: ["Reporting & Disclosure", "Investor & Stakeholder"],
    tags: ["Financial", "Annual Report", "Proxy"],
    image: "/reports/vaalco-annual.webp",
    covers: [
      { id: "vaalco-annual", src: "/reports/vaalco-annual.webp", alt: "Vaalco Energy 2025 Annual Report cover", title: "2025 Annual Report", short: "2025 Annual", dir: "/reports/flipbook/vaalco-annual", pages: 11, ratio: 1801 / 1382, pdf: "/reports/pdf/vaalco-annual.pdf" },
      { id: "vaalco-proxy", src: "/reports/vaalco-proxy.webp", alt: "Vaalco Energy 2026 Proxy Statement cover", title: "2026 Proxy Statement", short: "2026 Proxy", dir: "/reports/flipbook/vaalco-proxy", pages: 116, ratio: 1748 / 1342, pdf: "/reports/pdf/vaalco-proxy.pdf" },
    ],
    order: 6,
  },
  {
    id: "meritage-homes",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "Meritage Homes",
    category: "Sustainability Report",
    title: "Life. Built. Better.\n2023 Sustainability Report",
    context: "A national homebuilder communicating its sustainability strategy and progress.",
    challenge: "Make sustainability data accessible and engaging for a broad set of stakeholders.",
    role: "Sustainability reporting strategy, content, and design.",
    solution: "An integrated sustainability report that balances data, narrative, and brand in a warm, human way.",
    services: ["Reporting & Disclosure", "Digital"],
    tags: ["Impact", "Sustainability", "Integrated"],
    image: REPORTS.meritage,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "",
  },
  {
    id: "primary-energy",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "Primary Energy",
    category: "Sustainability",
    title: "Delivering Clean, Efficient &\nReliable Energy",
    context: "An energy provider reporting on sustainability performance and impact.",
    challenge: "Communicate a technical sustainability story with clarity and credibility.",
    role: "Sustainability report strategy, design, and production.",
    solution: "A framework-aligned sustainability report that makes impact tangible and easy to follow.",
    services: ["Reporting & Disclosure"],
    tags: ["Sustainability", "GRI", "TCFD"],
    image: REPORTS.primaryEnergy,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "/reports/pdf/primary-energy.pdf",
    flipbook: { dir: "/reports/flipbook/primary-energy", pages: 19, ratio: 612 / 792 },
  },
  {
    id: "flexitallic",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "Flexitallic",
    category: "Sustainability",
    title: "Making the World Safer & Cleaner — 2023 Sustainability",
    context: "An industrial manufacturer presenting sustainability performance to partners and customers.",
    challenge: "Position sustainability progress within a strong, recognizable brand system.",
    role: "Brand-aligned reporting, content, and design.",
    solution: "A sustainability performance report that reinforces brand equity while communicating measurable progress.",
    services: ["Reporting & Disclosure", "Corporate Communications"],
    tags: ["Sustainability", "Brand", "Design"],
    image: REPORTS.flexitallic,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "/reports/pdf/flexitallic.pdf",
    flipbook: { dir: "/reports/flipbook/flexitallic", pages: 34, ratio: 612 / 792 },
  },
  {
    id: "kennedy-wilson",
    segment: "Reporting & Communications",
    client: "Kennedy Wilson",
    category: "Annual Report",
    title: "Annual Report 2024",
    context: "A global real estate investment company reporting to investors.",
    challenge: "Present a diversified portfolio and financial story in a premium, investor-ready format.",
    role: "Reporting strategy, editorial design, and production.",
    solution: "A refined annual report with strong hierarchy and imagery that elevates the investor narrative.",
    services: ["Reporting & Disclosure", "Investor & Stakeholder"],
    tags: ["Annual Report", "Investor", "Real Estate"],
    image: REPORTS.kennedyWilson,
    order: 4,
    uniformCover: true,
    pdf: "/reports/pdf/kennedy-wilson.pdf",
    flipbook: {
      dir: "/reports/flipbook/kennedy-wilson",
      pages: 73,
      ratio: 720 / 504,
      chapters: [
        { label: "Shareholder Letter", page: 3 },
        { label: "Our Businesses", page: 4 },
        { label: "Financial Report", page: 13 },
        { label: "Management’s Discussion", page: 22 },
        { label: "Financial Statements", page: 42 },
        { label: "Notes to Financials", page: 46 },
      ],
    },
  },
  {
    id: "sbp",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "Specialty Building Products",
    category: "Sustainability",
    title: "2024 Corporate Sustainability Report",
    context: "A building products company communicating its sustainability journey.",
    challenge: "Introduce a corporate sustainability story to multiple stakeholder groups.",
    role: "Sustainability reporting strategy, content, and design.",
    solution: "A clear, credible corporate sustainability report built for a broad stakeholder audience.",
    services: ["Reporting & Disclosure"],
    tags: ["Sustainability", "Corporate Reporting"],
    image: REPORTS.sbp,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "",
  },
  {
    id: "longreach",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "The Longreach Group",
    category: "Responsible Investing",
    title: "2024 Responsible Investment Report",
    context: "A private investment firm reporting on responsible investment practices.",
    challenge: "Communicate a responsible investing framework to sophisticated stakeholders.",
    role: "Report strategy, content structure, and design.",
    solution: "A disciplined responsible investment report that reflects the firm’s rigor and standards.",
    services: ["Reporting & Disclosure", "Investor & Stakeholder"],
    tags: ["Responsible Investing", "Impact", "Reporting"],
    image: REPORTS.longreach,
    pdf: "/reports/pdf/longreach.pdf",
    flipbook: { dir: "/reports/flipbook/longreach", pages: 28, ratio: 1800 / 1277 },
  },
  {
    id: "blackberry-investor",
    segment: "Branding, Marketing & Employee Communication",
    client: "BlackBerry",
    category: "Investor Presentation",
    title: "Intelligent Security. Everywhere.",
    context: "A global software and security company communicating its strategy to investors.",
    challenge: "Position a transforming business with a confident, modern brand-led narrative.",
    role: "Investor presentation strategy, messaging, and design.",
    solution: "A polished investor presentation that pairs a bold brand system with a clear growth story.",
    services: ["Corporate Communications", "Investor & Stakeholder"],
    tags: ["Investor Presentation", "Brand", "Design"],
    image: REPORTS.pdfBlackberryInvestorCover,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "/reports/pdf/blackberry-investor.pdf",
    flipbook: { dir: "/reports/flipbook/blackberry-investor", pages: 27, ratio: 900 / 1600 },
  },
  {
    id: "blackberry-social",
    segment: "Branding, Marketing & Employee Communication",
    client: "BlackBerry",
    category: "Social Media Posts",
    title: "Q1 FY27 Social Campaign",
    context: "Amplifying quarterly performance and the growth story across social channels.",
    challenge: "Turn dense financial results into scroll-stopping, on-brand social content.",
    role: "Social content strategy, messaging, and design.",
    solution: "A set of crisp, on-brand social posts that make the quarter’s momentum easy to share.",
    services: ["Corporate Communications"],
    tags: ["Social Media", "Brand", "Campaign"],
    image: REPORTS.pdfBlackberryGrowth,
    images: [REPORTS.pdfBlackberryGrowth, REPORTS.pdfBlackberryGlance],
    smallCover: true,
    pdf: "",
  },
  {
    id: "dmi-solutions",
    segment: "Branding, Marketing & Employee Communication",
    client: "DMI Solutions",
    category: "Brand & Marketing",
    title: "The Name Behind the Names You Trust",
    context: "A development, manufacturing, and innovation company building its brand presence and marketing collateral.",
    challenge: "Craft a distinctive brand identity and marketing materials that convey capability and trust.",
    role: "Brand identity, marketing collateral, and design.",
    solution: "A bold, pattern-driven brand system and collateral that positions DMI as a trusted partner.",
    services: ["Brand & Identity", "Corporate Communications"],
    tags: ["Branding", "Marketing", "Website Infographic"],
    image: REPORTS.dmiSolutions,
    images: [REPORTS.dmiSolutions, REPORTS.dmiInnovaOne],
    smallCover: true,
    pdf: "",
  },
  {
    id: "climeco-brand",
    segment: "Branding, Marketing & Employee Communication",
    client: "ClimeCo",
    category: "Brand & Marketing",
    title: "Brand System & Marketing Collateral",
    context: "A sustainability and environmental commodities firm building a cohesive brand across touchpoints.",
    challenge: "Unify a wide range of materials under one confident, consistent brand system.",
    role: "Brand system, templates, and marketing collateral design.",
    solution: "A flexible brand system spanning presentations, flyers, stationery, infographics, and certificates.",
    services: ["Brand & Identity", "Corporate Communications"],
    tags: ["Branding", "Marketing", "Collateral"],
    image: REPORTS.climecoBrand,
    smallCover: true,
    pdf: "",
  },
  {
    id: "america-first-refining",
    segment: "Digital Media",
    client: "America First Refining",
    category: "Website",
    title: "Refining. Redefined.",
    context: "An energy company launching its digital presence.",
    challenge: "Introduce a bold new brand and story to the market online.",
    role: "Website design and build.",
    solution: "A confident, responsive brand website that tells the company’s energy story.",
    services: ["Digital Media", "Corporate Communications"],
    tags: ["Website", "Brand", "Responsive"],
    image: REPORTS.pdfAfr,
    uniformCover: true,
    framed: true,
    coverAspect: "aspect-[3/2]",
    url: "https://americafirstrefining.com/",
    pdf: "",
  },
  {
    id: "far-biotech",
    segment: "Digital Media",
    client: "FAR Biotech",
    category: "Website",
    title: "Quantum Leap in Drug Discovery",
    context: "A biotech company communicating breakthrough technology.",
    challenge: "Make complex quantum-AI science approachable and credible.",
    role: "Website design and build.",
    solution: "An immersive, animated website that makes the science clear and compelling.",
    services: ["Digital Media"],
    tags: ["Website", "Digital", "Responsive"],
    image: REPORTS.pdfFarBiotech,
    uniformCover: true,
    framed: true,
    coverAspect: "aspect-[3/2]",
    url: "https://www.farbiotech.com/",
    pdf: "",
  },
  {
    id: "american-campus-communities",
    segment: "Digital Media",
    client: "American Campus Communities",
    category: "Interactive Report",
    title: "Live Responsibly — Impact Update 2024",
    context: "A student-housing leader sharing its impact progress.",
    challenge: "Turn an impact report into an engaging digital experience.",
    role: "Interactive report design and build.",
    solution: "An interactive impact microsite that makes progress easy to explore.",
    services: ["Digital Media", "Sustainability"],
    tags: ["Interactive Report", "Sustainability", "Microsite"],
    image: REPORTS.pdfAcc,
    uniformCover: true,
    framed: true,
    coverAspect: "aspect-[3/2]",
    url: "https://impact.americancampus.com/",
    pdf: "",
  },
  {
    id: "kennedy-wilson-esg",
    segment: "Digital Media",
    client: "Kennedy Wilson",
    category: "Interactive Report",
    title: "2025 Sustainability Report",
    context: "A global real estate investment company reporting on its sustainability performance.",
    challenge: "Present a comprehensive sustainability report in an accessible digital format.",
    role: "Interactive sustainability report design and build.",
    solution: "A polished interactive sustainability microsite built for stakeholders and investors.",
    services: ["Digital Media", "Sustainability"],
    tags: ["Interactive Report", "Sustainability", "Microsite"],
    image: REPORTS.pdfKennedyWilsonEsg,
    uniformCover: true,
    framed: true,
    coverAspect: "aspect-[3/2]",
    url: "https://esg.kennedywilson.com/",
    pdf: "",
  },
  {
    id: "cemex",
    segment: "Reporting & Communications",
    client: "CEMEX",
    category: "Integrated Report",
    title: "2025 Integrated Report",
    context: "A global building materials company communicating strategy, performance, and value creation to investors and stakeholders.",
    challenge: "Bring a large, complex global business into a single, cohesive, investor-ready story.",
    role: "Reporting strategy, narrative structure, design, and production.",
    solution: "An integrated report that connects financial and sustainability performance in one clear, credible narrative.",
    services: ["Reporting & Disclosure", "Investor & Stakeholder"],
    tags: ["Integrated Report", "Investor", "Design"],
    image: REPORTS.pdfCemex,
    lead: true,
    order: 1,
    pdf: "/reports/pdf/cemex.pdf",
    flipbook: {
      dir: "/reports/flipbook/cemex",
      pages: 289,
      ratio: 800 / 1280,
      chapters: [
        { label: "Our Company", page: 4 },
        { label: "Our Strategy", page: 13 },
        { label: "Financial Performance", page: 25 },
        { label: "Future in Action", page: 29 },
        { label: "Stakeholder Engagement", page: 68 },
        { label: "Governance", page: 114 },
        { label: "Appendix", page: 157 },
      ],
    },
  },
  {
    id: "kodiak",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "Kodiak Gas Services",
    category: "Sustainability Report",
    title: "Built in the Field. Proven Every Day.",
    context: "An energy infrastructure company reporting on its sustainability performance and operational impact.",
    challenge: "Communicate a field-driven, operational sustainability story with clarity and credibility.",
    role: "Sustainability reporting strategy, content, design, and production.",
    solution: "A grounded, image-led sustainability report that connects day-to-day operations to measurable progress.",
    services: ["Reporting & Disclosure", "Corporate Communications"],
    tags: ["Sustainability", "Energy", "Design"],
    image: REPORTS.pdfKodiak,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "/reports/pdf/kodiak.pdf",
    flipbook: { dir: "/reports/flipbook/kodiak", pages: 32, ratio: 612 / 1008 },
  },
  {
    id: "climeco",
    segment: "Sustainability Strategy, Reporting & Advisory",
    client: "ClimeCo",
    category: "Impact Report",
    title: "Impact Report",
    context: "A sustainability and environmental commodities firm sharing its impact and progress with clients and partners.",
    challenge: "Turn a broad set of climate and impact initiatives into a clear, engaging narrative.",
    role: "Impact reporting strategy, content, and design.",
    solution: "An impact report that makes climate progress tangible and easy to explore for a broad audience.",
    services: ["Reporting & Disclosure", "Digital"],
    tags: ["Impact", "Sustainability", "Climate"],
    image: REPORTS.pdfClimeco,
    uniformCover: true,
    coverAspect: "aspect-[3/2]",
    pdf: "/reports/pdf/climeco.pdf",
    flipbook: { dir: "/reports/flipbook/climeco", pages: 42, ratio: 850 / 1400 },
  },
];

export const WORK_SEGMENTS = [
  {
    id: "corporate-reports",
    label: "Reporting & Communications",
    blurb: "Annual, integrated, and investor-ready communications that turn performance into a clear, credible narrative.",
  },
  {
    id: "sustainability-strategy-advisory",
    label: "Sustainability Strategy, Reporting & Advisory",
    blurb: "Sustainability and responsible-investment communications that connect strategy, performance, data, disclosure, and stakeholder expectations.",
  },
  {
    id: "branding-marketing-employee",
    label: "Branding, Marketing & Employee Communication",
    blurb: "Brand systems, marketing campaigns, and employee and executive communications that keep messaging consistent and on-brand.",
  },
  {
    id: "interactive-media",
    label: "Digital Media",
    blurb: "Report microsites, digital experiences, and digital platforms built for exploration and engagement.",
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "Joel and his team have a very practical and efficient approach, and great ideas on the presentation and design of our report. Very responsive and easy to work with.",
    name: "Howard King",
    company: "Flexitallic",
  },
  {
    quote:
      "We’ve worked with the Big Pivot team on a number of our sustainability reports, as well as investor brochures and decks. They do fantastic graphic design work, on schedule and on budget.",
    name: "Kaitlyn Allen",
    company: "ClimeCo",
  },
  {
    quote:
      "Joel and I had crossed paths many times and I always knew he was a class act — and a great professional. That proved more than true this year as we completed our first sustainability report. It’s a well done book and I’m glad to have had the benefit of his time and talents on this very important project.",
    name: "John Breed",
    company: "ChampionX",
  },
  {
    quote:
      "The Big Pivot team helped bring our inaugural sustainability report to life. They were incredibly responsive, timely, and collaborative throughout our design process, and transparent about budget and timeline. I would absolutely recommend their services to others.",
    name: "Alexis Webb",
    company: "EnVen Energy",
  },
];

export const ABOUT = {
  heroHeadline: "Turn Complexity Into Clarity",
  heroSub:
    "Senior experience, disciplined execution, and a genuine partnership model — on every engagement.",
  storyHeadline: "Experience You Can Trust When It Matters Most.",
  story: [
    "Big Pivot Partners is a strategic stakeholder communications firm specializing in corporate reporting, sustainability strategy and reporting, investor and stakeholder communications, digital media, and strategic creative solutions.",
    "Senior experience, disciplined execution, and a genuine partnership model — on every engagement. Founded in 2014 by Marc Seligson after more than two decades with the nation’s largest annual report design firm, Big Pivot Partners was later joined by Joel Charron, who brought more than 30 years of experience in stakeholder communications, sustainability solutions, reporting, and strategic advisory.",
    "We work with organizations ranging from start-ups and IPOs to Fortune 500 companies, delivering award-winning annual, sustainability, investor, and stakeholder communications across print and digital platforms.",
    "By aligning business strategy, performance, and stakeholder priorities, we transform complex information into compelling communications — building trust, improving efficiency, and supporting long-term growth.",
  ],
  storyCallout:
    "Big Pivot is a strategic stakeholder communications firm and solutions provider. Our senior team brings decades of experience across corporate reporting, investor and stakeholder communications, sustainability, digital, and strategic advisory.",
  values: [
    { title: "Clarity", text: "We turn complex information into communications stakeholders can understand and use." },
    { title: "Credibility", text: "We bring the rigor, professionalism, and quality expected in high-stakes stakeholder communications." },
    { title: "Strategy", text: "We connect business priorities and stakeholder needs to messaging and execution." },
    { title: "Execution", text: "Senior involvement, disciplined project management, and dependable delivery." },
    { title: "Integration", text: "Consistent communications across print, digital, and presentation channels." },
  ],
  sectors: [
    "Energy", "Industrial", "Real Estate", "Healthcare", "Retail",
    "Financial Services", "Technology", "Transportation", "Hospitality",
    "Sports & Events",
  ],
  leadership: [
    {
      name: "Marc Seligson",
      role: "Founder & President",
      email: "marc@bigpivot.net",
      linkedin: "https://www.linkedin.com/in/marcseligson",
      photo: "/team/marc.webp",
      bio: "Marc founded Big Pivot Partners in 2014 after more than two decades with the nation’s largest annual report design firm, and leads strategy and creative direction across the firm’s most important engagements.",
    },
    {
      name: "Joel Charron",
      role: "Partner",
      email: "joel@bigpivot.net",
      linkedin: "https://www.linkedin.com/in/joelcharron",
      photo: "/team/joel.webp",
      bio: "Joel brings more than 30 years of experience in stakeholder communications, sustainability solutions, reporting, and strategic advisory. A certified sustainability reporting professional, he leads client strategy and delivery.",
    },
  ],
};

export const CLIENTS = [
  "Cemex", "Kodiak Gas Services", "Penguin Solutions", "American Campus Communities",
  "ClimeCo", "Kennedy Wilson", "America First Refining", "BlackBerry",
  "eHealth", "CKE Restaurants", "Meritage Homes", "Primary Energy",
];

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Our Work" },
  { to: "/insights", label: "Insights & Updates" },
  { to: "/awards", label: "Recognition" },
  { to: "/contact#report-analysis", label: "Free Report Analysis" },
];

export const PROJECT_TYPES = [
  "Sustainability / impact report",
  "Annual or integrated report",
  "Investor presentation or communications",
  "Employee or executive communications",
  "Digital solution",
  "Corporate communications / creative support",
  "Other",
];

export const TIMING_OPTIONS = [
  "Exploring options",
  "Within 1–3 months",
  "Within 3–6 months",
  "6+ months",
];


export const ANNOUNCEMENT = {
  eyebrow: "New · Stakeholder Communications Alignment",
  title: "Do Your Communications Tell the Same Company Story?",
  meta: "October 2026",
  ctaLabel: "Explore Stakeholder Communications Alignment",
  ctaTo: "/stakeholder-communications-review",
  content: [
    { type: "lead", text: "Introducing Stakeholder Communications Alignment from Big Pivot Partners." },
    { type: "p", text: "An investor presentation explains where the company is headed. A sustainability report describes its commitments and progress. The website introduces the business, while employee communications help people understand their role in its success." },
    { type: "p", text: "Each may work well on its own. The question is whether they work together." },
    { type: "p", text: "Do they reinforce the same priorities? Do the claims connect to evidence? Can someone moving between them understand the company’s strategy, performance and long-term value?" },
    { type: "p", text: "When those connections are weak, stakeholders are left to make sense of the differences themselves. A strategic priority may feature prominently in one presentation and barely appear on the website. A meaningful achievement may be buried in a report. A commitment may be repeated across channels without a clear explanation of progress." },
    { type: "p", text: "Those gaps can make a company’s story less credible—or cause it to receive less credit than its work deserves." },
    { type: "h3", text: "A clearer view of how your communications connect" },
    { type: "p", text: "Big Pivot Partners developed Stakeholder Communications Alignment to help companies look across selected materials and assess how well they support one clear, credible company story." },
    { type: "p", text: "We review where messages connect, where they drift and how content and design can strengthen the overall story. Depending on the scope, those materials might include corporate websites, investor presentations, annual and sustainability reports, or employee communications." },
    { type: "p", text: "The review considers practical questions:" },
    { type: "list", items: [
      "Consistency: Do the materials reinforce the same strategy and priorities?",
      "Credibility: Are key claims supported by clear evidence and appropriate context?",
      "Clarity: Can each audience find and understand what matters to them?",
      "Visibility: Are meaningful strengths and achievements getting the attention they deserve?",
      "Design: Do structure, hierarchy and presentation help people understand the story?",
    ] },
    { type: "p", text: "The goal is to identify what works and where improvements would make the greatest difference." },
    { type: "h3", text: "One story, with room for different audiences" },
    { type: "p", text: "Alignment doesn’t mean using the same language everywhere. Investors, employees, customers and other stakeholders have different questions. Communications should reflect those needs while remaining grounded in a shared understanding of the business." },
    { type: "p", text: "For example, if operational efficiency is a strategic priority, an investor presentation might explain its financial contribution. A sustainability report might show improvements in energy or resource use. Employee communications might explain the changes people are being asked to make." },
    { type: "p", text: "Each serves a different purpose. Together, they should make the strategy easier to understand." },
    { type: "p", text: "Design matters here, too. What gets emphasized, how information is organized and whether results are easy to interpret all influence what stakeholders take away." },
    { type: "h3", text: "A practical place to start" },
    { type: "p", text: "Stakeholder Communications Alignment offers a focused starting point for teams preparing for a reporting cycle, refreshing their website, updating investor materials or communicating a change in direction." },
    { type: "p", text: "It brings the materials into one conversation: what is the company trying to communicate, what will stakeholders actually understand, and where would clearer content or stronger design help?" },
  ],
};


export const INSIGHTS = [
  {
    slug: "investors-begin-to-price-climate-risk",
    title: "Investors Are Starting to Price Climate Risk: What Robeco’s 2026 Survey Means for Reporting",
    metaTitle: "Investors Price Climate Risk: Robeco 2026 Survey",
    category: "Markets",
    date: "October 2026",
    readTime: "4 min read",
    image: "https://images.unsplash.com/photo-1512403754473-27835f7b9984?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
    related: ["us-climate-disclosure-landscape", "from-commitments-to-evidence"],
    externalSource: {
      label: "ESG Today: 82% of investors expect climate risks to impact asset prices",
      url: "https://www.esgtoday.com/82-of-investors-expect-climate-risks-to-impact-asset-prices-over-next-decade-robeco-survey/",
    },
    metaDescription:
      "Robeco’s 2026 survey finds 82% of investors expect physical climate risks to hit asset prices within a decade. Climate has moved from ambition to a pricing question — and that raises the bar for credible reporting.",
    excerpt:
      "A new Robeco survey of 300 institutional investors representing over $35 trillion finds climate risk moving from a values conversation to a pricing one: 82% expect physical climate risks to affect asset prices within ten years. When capital starts pricing risk, vague reporting stops being an option.",
    content: [
      { type: "p", text: "For several years the story about climate and investors ran in one direction — backlash, retreat, and a quiet walking-back of commitments. Robeco’s 2026 Global Climate Investing Survey points the other way. Based on 300 institutional and wholesale investors across Europe, North America, Asia-Pacific and South Africa — representing more than $35 trillion in assets — it finds climate moving from a stated ambition to a pricing question." },
      { type: "h2", text: "Climate risk is becoming a pricing input" },
      { type: "p", text: "The headline number is hard to ignore: 82% of investors expect physical climate risks to have a significant or moderate impact on asset prices over the next ten years, with 46% expecting a “significant” impact. Two-thirds (66%) see that impact within just five years. A majority also expect physical risk to shape strategic asset allocation (63%), stock picking (59%) and even asset-manager selection (57%) over the next five years." },
      { type: "quote", text: "Climate has moved from a stated ambition to a pricing question, and a pricing question cannot be set aside when the mood turns. — Lucian Peppelenbos, Climate & Biodiversity Strategist, Robeco" },
      { type: "p", text: "After peaking in 2022, the share of investors calling climate central to their investment policy has ticked up for the first time since the backlash began — 47% this year, led by a rebound in North America (still the laggard at 27%). Looking ahead, 62% expect climate to be significant or central to policy within two years." },
      { type: "h2", text: "Commitments are holding — and capital is moving" },
      { type: "list", items: [
        "Among investors with a net-zero commitment, 64% say the global slowdown in government support has not changed their goal or approach; only 4% are reconsidering the commitment itself",
        "60% plan to increase allocations to climate-solution investments over the next three years (42% within the next year)",
        "72% have either set a quantitative target or a general aim to invest more in climate solutions — up from 64% a year ago",
        "Yet investors are clear-eyed: only 19% expect an orderly transition this decade, and 44% see the 2°C Paris target as unachievable",
      ] },
      { type: "h2", text: "What it means for how you report" },
      { type: "p", text: "When investors start treating climate as a pricing variable rather than a values statement, the quality of your climate and sustainability communication becomes a financial issue, not a reputational one. Decision-useful, evidence-backed disclosure — connected to strategy, risk and long-term value — is what lets capital price your company accurately instead of applying a discount for uncertainty." },
      { type: "p", text: "This is the same shift we see everywhere in the market: frameworks are the structure, not the story; credibility beats volume; and sustainability has to live inside the business. Big Pivot Partners helps companies turn a fragmented, fast-moving disclosure landscape into a clear, credible narrative that investors can actually use — working alongside the legal, accounting and assurance advisers who own the technical compliance questions." },
    ],
  },

  {
    slug: "reporting-on-the-sdgs-with-credibility",
    title: "Reporting on the SDGs: From Logos to Evidence",
    category: "Sustainability",
    date: "September 2026",
    readTime: "5 min read",
    image: REPORTS.sdgs,
    homeTileImage: REPORTS.sdgsGoals,
    related: ["revised-esrs-what-the-final-changes-mean", "sustainability-reporting-beyond-design"],
    externalSource: {
      label: "GRI: Integrating the SDGs into sustainability reporting",
      url: "https://www.globalreporting.org/public-policy/sustainable-development/integrating-sdgs-into-sustainability-reporting",
    },
    metaDescription:
      "Move SDG reporting from a page of logos to real evidence—connect the goals to material issues, measurable targets and the standards you already report against.",
    excerpt:
      "The UN’s 17 Sustainable Development Goals give companies a shared language for their impact. But a page of colorful icons proves nothing. GRI’s guidance points to a more credible path — connect the goals to material issues, measurable targets, and the standards you already report against.",
    content: [
      { type: "p", text: "The 17 Sustainable Development Goals have become a common shorthand for corporate purpose. They are easy to display and easy to admire — which is exactly the problem. Too many reports treat the SDGs as a decorative layer: a grid of icons mapped loosely to activities the company was doing anyway. GRI’s guidance on integrating the SDGs into reporting points in a more demanding, and more useful, direction — one built on transparency, materiality, and evidence." },
      { type: "h2", text: "The icons are the easy part" },
      { type: "p", text: "Target 12.6 of the SDGs asks companies to adopt sustainable practices and integrate sustainability information into their reporting. The intent is not to collect logos; it is to help organizations understand, communicate, and actually manage their contributions. A serious reader — an investor, a regulator, a procurement team — can tell the difference in seconds between a company reporting against goals it genuinely moves and one wallpapering a report with all seventeen." },
      { type: "h2", text: "Start with what’s material" },
      { type: "p", text: "GRI’s practical guide frames SDG reporting as a disciplined, step-by-step process: identify the priority goals where the business has real positive and negative impact, measure and analyze performance against them, and then report and integrate the results into decision-making. Prioritization is the whole game. A company that reports credibly on the three or four goals it truly affects will always be more persuasive than one claiming a stake in all of them." },
      { type: "quote", text: "The SDGs are not a menu to sample from. They are a lens for showing where your business genuinely moves the needle — and being honest about where it doesn’t." },
      { type: "h2", text: "Link the goals to standards you already use" },
      { type: "p", text: "One of the most practical resources GRI offers is a linkage between the SDGs and the GRI Standards, alongside its analysis of the goals and targets that draws on metrics from CDP, SASB, the UN Global Compact, and others. The lesson for reporting teams is to stop treating the SDGs as a separate exercise. Map them to the disclosures and data you are already producing — including under IFRS S1 and S2, GRI, and SASB — so SDG reporting reinforces the rest of the report instead of bolting a parallel story onto it." },
      { type: "list", items: [
        "Prioritize the goals where your business has genuine, measurable impact — positive and negative",
        "Connect each goal to specific, evidence-backed targets rather than aspirations",
        "Map SDG disclosures to the standards you already report against, so the data reconciles",
        "Be candid about gaps and trade-offs; selective optimism erodes the credibility you’re trying to build",
      ] },
      { type: "h2", text: "Speak to investors, not just stakeholders" },
      { type: "p", text: "GRI’s guidance on addressing investor needs makes an important point: SDG information is increasingly read by capital providers, not only civil society. That raises the bar. Investors want contribution framed in terms of strategy, risk, and long-term value — comparable, decision-useful, and consistent with the financial story. SDG reporting that stays vague or promotional simply gets ignored by the audience with the most influence over the company’s future." },
      { type: "p", text: "At Big Pivot Partners, we help companies turn the SDGs from a graphic into a genuine part of the business narrative — connected to material issues, grounded in measurable targets, and consistent across every disclosure. We work on the strategy, story, and design side of that shift, alongside the standards and assurance partners who own the technical detail. GRI’s tools for integrating the SDGs are a strong place to start." },
    ],
  },
  {
    slug: "from-commitments-to-evidence",
    title: "From Commitments to Evidence: What IKEA’s Climate Week Message Confirms",
    metaTitle: "IKEA’s Climate Week Message: Commitments to Evidence",
    category: "Perspective",
    date: "September 2026",
    readTime: "4 min read",
    image: REPORTS.performance,
    related: ["revised-esrs-what-the-final-changes-mean", "us-climate-disclosure-landscape"],
    externalSource: {
      label: "Read IKEA’s op-ed: “Navigating change, staying focused”",
      url: "https://www.ikea.com/global/en/newsroom/sustainability/navigating-change-staying-focused-260924/",
    },
    metaDescription:
      "Sustainability reporting is shifting from commitments to evidence. Why saying less until you can prove more is a discipline, not a retreat—lessons from IKEA.",
    excerpt:
      "IKEA’s Chief Sustainability Officer used Climate Week to make a point we’ve been making all year: sustainability is moving from an era of commitments to an era of evidence. Companies may \u201Csay less until they can demonstrate more\u201D — and that’s not a retreat. It’s a discipline.",
    content: [
      { type: "p", text: "As Climate Week NYC wrapped, IKEA’s Chief Sustainability Officer, Lena Julle, published a short, clear-eyed op-ed on where sustainability communication is headed. Her argument mirrors the one we’ve been making: the era defined by commitments is giving way to an era defined by evidence." },
      { type: "h2", text: "From commitments to evidence" },
      { type: "p", text: "For years, sustainability was shaped around targets — the changes companies said they wanted to create. That focus mattered; it moved sustainability from the sidelines to the centre of strategy. But evolving rules, including the EU’s CSRD and its Empowering Consumers Directive, now expect companies to show measurable progress and substantiate what they claim. The bar has shifted from stating intent to proving it." },
      { type: "quote", text: "Companies may \u201Csay less until they can demonstrate more.\u201D — Lena Julle, Chief Sustainability Officer, Inter IKEA Group" },
      { type: "p", text: "That line captures the whole shift in a sentence. Saying less until you can demonstrate more is not a retreat from ambition — it is a move toward disciplined, defensible communication. Less volume, more substantiation. As IKEA puts it, frameworks, methodologies and even targets may evolve, but the underlying work — renewable energy, materials, supply chains, and integrating sustainability into everyday decisions — continues." },
      { type: "h2", text: "Where we’d go a step further" },
      { type: "p", text: "IKEA is understandably careful about tying sustainability directly to financial performance, framing it instead around competitiveness, resilience and investment confidence. We think the stronger position for most companies is to go further: connect strategy, financial performance, sustainability, risk and governance into one corporate story. Reporting is no longer the objective — it is the evidence layer for what the business is actually doing." },
      { type: "p", text: "It reinforces the position now at the core of our work: frameworks are the structure, not the story; credibility beats volume; and sustainability has to live inside the business. As CSRD, SEC expectations, California’s climate laws and voluntary practice keep shifting, the durable proposition isn’t \u201Cmore reporting\u201D — it is helping companies explain how sustainability fits into the business, credibly, clearly, and with evidence." },
    ],
  },
  {
    slug: "revised-esrs-what-the-final-changes-mean",
    title: "The ESRS Just Got Simpler: What the Final Changes Mean for Sustainability Reporting",
    metaTitle: "The Revised ESRS: What the Final Changes Mean",
    category: "Disclosure",
    date: "September 2026",
    readTime: "6 min read",
    image: REPORTS.climecoBrand,
    related: ["us-climate-disclosure-landscape", "california-sb-253-first-reporting-cycle"],
    metaDescription:
      "The EU finalized a major ESRS simplification—about 60% fewer data points, sharper materiality, and sustainability tied to the business story.",
    excerpt:
      "The EU has finalized a major simplification of its sustainability reporting standards — fewer required data points, a sharper focus on what’s material, and a stronger push to connect sustainability to the business story. Here’s what it changes, in plain terms.",
    content: [
      { type: "p", text: "The European Union has finalized a significant simplification of its sustainability reporting rules. As part of the Omnibus I package, the European Sustainability Reporting Standards (ESRS) — the standards behind the EU’s Corporate Sustainability Reporting Directive (CSRD) — have been revised to be shorter and more focused. The updated standards apply to financial years beginning on or after 1 January 2027, and companies can choose to adopt them early." },
      { type: "h2", text: "Less to report, not less to say" },
      { type: "p", text: "The biggest change is volume. The number of required data points has been cut by roughly 60 percent, with more emphasis on clear, quantitative information and less on long stretches of narrative. The rules also draw a sharper line between what a company must report and what is optional. The intent is not to say less about sustainability — it is to say it more clearly, with far less duplication." },
      { type: "h2", text: "Materiality still decides what matters" },
      { type: "p", text: "The revised standards keep double materiality at their core: a company reports on the sustainability issues that affect its business, and on the issues where its business affects people and the environment. What’s new is clearer guidance on how to make that judgment. A well-reasoned, well-documented view of what is material remains the foundation of a credible report." },
      { type: "quote", text: "The revision is an invitation to report with more focus and less clutter — and to connect sustainability to the business story rather than bury it in data." },
      { type: "h2", text: "One connected story" },
      { type: "p", text: "A central theme of the new rules is connection: sustainability information should line up with the financial statements and the rest of the annual report, using consistent numbers and assumptions. That is exactly the alignment we help clients get right — so the sustainability report, the annual report, and investor materials tell one coherent story instead of three that don’t quite match." },
      { type: "h2", text: "Working with global standards, not against them" },
      { type: "p", text: "The revised ESRS were built to sit more comfortably alongside the global standards many companies already use, including IFRS S1 and S2 from the ISSB. Where a topic isn’t fully covered, the standards point to familiar references such as GRI and SASB. For companies that report in more than one market, that alignment reduces — though it doesn’t remove — the effort of managing several frameworks at once." },
      { type: "h2", text: "What to do now" },
      { type: "list", items: [
        "Revisit what is material to your business, and write down the reasoning",
        "Check which of today’s disclosures stay required, become optional, or drop away",
        "Tighten the links between your sustainability report, financial statements, and investor materials",
        "Decide whether to adopt the revised standards early or move across for 2027",
      ] },
      { type: "p", text: "Big Pivot Partners works on the strategy, story, design, and disclosure-alignment side of this shift — turning a complex rule change into clearer, better-connected reporting. We work alongside the legal, accounting, and assurance advisers who own the technical compliance and audit questions. The official texts are available from the European Commission, EFRAG, and EUR-Lex." },
    ],
  },
  {
    slug: "us-climate-disclosure-landscape",
    title: "The U.S. Climate Disclosure Picture: California Leads, Washington Waits",
    metaTitle: "The U.S. Climate Disclosure Picture",
    category: "Disclosure",
    date: "September 2026",
    readTime: "5 min read",
    image: REPORTS.primaryEnergy,
    related: ["california-sb-253-first-reporting-cycle", "revised-esrs-what-the-final-changes-mean"],
    metaDescription:
      "The U.S. climate disclosure picture is fragmented: California leads, the federal SEC rule is on hold, and many companies keep reporting anyway.",
    excerpt:
      "While Europe finalizes its sustainability rules, the U.S. picture is more fragmented — a strong state lead from California, a federal rule in limbo, and companies choosing to keep reporting anyway. Here’s the shape of it.",
    content: [
      { type: "p", text: "For U.S. companies, the question isn’t whether climate disclosure is coming — it’s which rules will actually apply. Unlike Europe’s single, harmonized framework, the American landscape is a patchwork: a strong lead from California, a federal rule caught in legal and political crosscurrents, and a large group of companies that keep reporting regardless because investors and customers expect it." },
      { type: "h2", text: "California sets the pace" },
      { type: "p", text: "Two California laws now anchor the U.S. conversation. SB 253, the Climate Corporate Data Accountability Act, requires large companies doing business in the state to report their greenhouse gas emissions. SB 261, the Climate-Related Financial Risk Act, requires a broader set of companies to publish how climate change could affect their business and finances. Because so many national companies do business in California, these state laws effectively set a floor for much of corporate America." },
      { type: "quote", text: "In practice, California has become the country’s climate-disclosure baseline — even for companies headquartered nowhere near it." },
      { type: "h2", text: "The federal rule is on hold" },
      { type: "p", text: "The Securities and Exchange Commission adopted its own climate disclosure rule in 2024, but it was paused almost immediately amid legal challenges, and the agency later stepped back from defending it. For now, there is no active federal climate reporting requirement — leaving companies to look to the states and to voluntary standards for direction." },
      { type: "h2", text: "Voluntary standards fill the gap" },
      { type: "p", text: "With the federal picture unsettled, many companies anchor their reporting to widely used standards — the ISSB’s IFRS S1 and S2, the climate-risk approach popularized by the TCFD, and topic guidance from SASB and GRI. These give investors comparable, decision-useful information no matter how the regulatory debate plays out." },
      { type: "h2", text: "What it means for reporting" },
      { type: "list", items: [
        "Confirm whether California’s SB 253 and SB 261 thresholds apply to you",
        "Treat a credible, well-documented climate-risk narrative as table stakes, regardless of the federal rule",
        "Keep your emissions data and methodology consistent across every channel that references them",
        "Anchor voluntary disclosure to a recognized standard so it stays comparable and durable",
      ] },
      { type: "p", text: "Big Pivot Partners helps U.S. companies turn this fragmented picture into clear, credible, and consistent communications — connecting climate and sustainability information to strategy, performance, and long-term value. We work alongside the legal, accounting, and assurance advisers who own the technical compliance questions. For the mechanics of California’s first reporting cycle, see our companion piece on SB 253." },
    ],
  },

  {
    slug: "california-sb-253-first-reporting-cycle",
    title: "SB 253 Arrives: What California’s First GHG Reporting Cycle Really Asks Of You",
    metaTitle: "SB 253: California’s First GHG Reporting Cycle",
    category: "Disclosure",
    date: "September 2026",
    readTime: "5 min read",
    image: REPORTS.sb253,
    related: ["us-climate-disclosure-landscape", "revised-esrs-what-the-final-changes-mean"],
    pdf: "/reports/pdf/sb253-carb-guidance.pdf",
    pdfLabel: "Download CARB’s SB 253 guidance (PDF)",
    deadline: { label: "First SB 253 submittals due", date: "November 10, 2026" },
    metaDescription:
      "California’s first SB 253 GHG submittals are due Nov 10, 2026. With CARB granting year-one enforcement discretion, treat it as architecture, not a scramble.",
    excerpt:
      "California’s first SB 253 greenhouse gas submittals are due November 10, 2026. The Air Resources Board is leading with enforcement discretion for year one — which makes this less a compliance scramble and more an opportunity to build the reporting architecture you’ll rely on for years.",
    content: [
      { type: "p", text: "For the first time, a large group of companies doing business in California must publicly report their greenhouse gas emissions. Senate Bill 253 — the Climate Corporate Data Accountability Act — moves from statute to practice this year, with the first submittals due to the California Air Resources Board (CARB) on November 10, 2026." },
      { type: "p", text: "CARB’s guidance for this first cycle is notable for its tone. Rather than opening with penalties, it opens with pragmatism. For 2026, CARB will exercise enforcement discretion — a signal that the agency wants companies to start the habit of reporting well, not to be punished for imperfect first attempts." },
      { type: "h2", text: "What the first cycle actually requires" },
      { type: "p", text: "Reporting entities are expected to disclose Scope 1 and Scope 2 emissions for a prior fiscal year, drawn from information they already possess or were reasonably collecting as of December 5, 2024. Companies that were not yet gathering that data by then are not expected to submit Scope 1 and 2 figures for this first cycle, though CARB encourages them to file a statement of non-reporting rather than stay silent." },
      { type: "p", text: "Limited assurance formally enters the picture in 2026, but here too CARB’s enforcement discretion applies: submissions will be accepted without assurance for this first year. The direction of travel is clear even if the first step is gentle — assured, comparable emissions data is where this is heading." },
      { type: "quote", text: "CARB is asking for a credible start, not a perfect finish. The companies that treat year one as foundation-building will be the ones ready when the discretion ends." },
      { type: "h2", text: "The detail that separates a filing from a report" },
      { type: "p", text: "CARB encourages companies to add context — methodologies, boundaries, the emission-factor datasets used for Scope 2, and a candid account of what is and isn’t captured yet. That is where a bare compliance filing becomes something a reader can actually trust and interpret. The choices you document now — organizational boundaries, data sources, Scope 2 factor options — become the baseline every future cycle is measured against." },
      { type: "list", items: [
        "Confirm whether you meet the reporting-entity threshold and which fiscal year you’ll report",
        "Assemble the Scope 1 and Scope 2 data you already hold, and document its sources and boundaries",
        "Decide and record your Scope 2 emission-factor approach so it’s consistent year over year",
        "If you can’t report yet, prepare a clear statement of non-reporting rather than going dark",
        "Treat assurance-readiness as a 2027 goal you begin building toward now",
      ] },
      { type: "h2", text: "Why this is a communications question, not just a compliance one" },
      { type: "p", text: "SB 253 data will not live in isolation. It will be read alongside your annual report, your sustainability disclosures, and your investor materials — and it needs to tell the same story they do. The risk in year one is not the calculation; it is disconnection, where a new regulatory filing says something subtly different from everything else you publish." },
      { type: "p", text: "At Big Pivot Partners, we see the first SB 253 cycle as an architecture moment. Get the boundaries, the data, and the narrative right once, and every future submittal — assured, disaggregated, and scrutinized — becomes an extension of a story you already tell clearly. The full CARB guidance is available below." },
    ],
  },
  {
    slug: "modernising-corporate-reporting-uk",
    title: "From Compliance to Communication: The Next Era of Corporate Reporting",
    metaTitle: "The Next Era of Corporate Reporting",
    category: "Disclosure",
    date: "September 2026",
    readTime: "4 min read",
    image: REPORTS.stepan,
    metaDescription:
      "The UK’s 2026 consultation signals a shift from disclosure volume to financially material, decision-useful communication—the next era of corporate reporting.",
    excerpt:
      "The UK’s September 2026 consultation points to a broader market shift — from disclosure volume to financially material, decision-useful communication. The next era of reporting will be defined by better communication, not more information.",
    content: [
      { type: "p", text: "Corporate reporting has spent years adding more information." },
      { type: "p", text: "The next challenge is helping stakeholders understand what that information means." },
      { type: "p", text: "The UK government’s September 2026 consultation on modernising corporate reporting points toward a broader shift in the market: less emphasis on disclosure volume and more emphasis on financially material, decision-useful communication." },
      { type: "p", text: "The proposed direction is clear. Reporting should be simpler, more focused, more flexible and better connected to the needs of investors and creditors. Companies should also have greater freedom to tell their own business story rather than relying on overly prescriptive disclosure structures." },
      { type: "p", text: "One of the most important proposals is a simpler strategic reporting framework built around five core areas: business model, performance, resources and relationships, strategy, and risk." },
      { type: "p", text: "That matters because stakeholders do not experience those areas separately." },
      { type: "p", text: "They see one company." },
      { type: "p", text: "The strongest corporate reporting connects strategy, financial performance, sustainability, risk and governance into one coherent narrative instead of forcing readers to assemble the story themselves." },
      { type: "p", text: "The consultation also reinforces an important shift in sustainability reporting. Sustainability is not disappearing. Where it is financially material, it is increasingly becoming part of the broader business story. The UK government continues to support the financial-materiality approach behind the ISSB and UK Sustainability Reporting Standards while considering greater flexibility in where sustainability information appears within the annual report." },
      { type: "p", text: "The digital side of reporting is evolving as well. The consultation considers digital-first communications, greater electronic tagging, website and portal reporting, machine-readable formats and the growing role of AI in how corporate information is prepared and analyzed." },
      { type: "p", text: "That means the future corporate report will increasingly need to work for both people and technology." },
      { type: "p", text: "For companies, the implication is straightforward: better reporting starts before design." },
      { type: "p", text: "It starts with reporting architecture." },
      { type: "p", text: "What does management need stakeholders to understand? What information is truly material? Where should it live? How do the pieces connect? Which disclosures explain performance, and which simply add volume?" },
      { type: "p", text: "At Big Pivot Partners, we believe the next era of corporate reporting will be defined not by more information, but by better communication." },
      { type: "p", text: "Strategy. Performance. Sustainability. Risk. Governance. One clear, credible business story." },
      { type: "p", text: "Trust. Clarity. Credibility." },
    ],
  },
  {
    slug: "when-disclosure-rules-shift",
    title: "When Disclosure Rules Shift, Your Story Still Has to Hold Together",
    metaTitle: "When Disclosure Rules Shift",
    category: "Disclosure",
    date: "September 2026",
    readTime: "5 min read",
    image: REPORTS.pdfCemex,
    metaDescription:
      "Requirements and frameworks keep changing. What matters is whether your strategy, risks, performance and targets stay consistent across every disclosure channel.",
    excerpt:
      "Requirements will keep changing and frameworks will keep evolving. What matters is whether your strategy, risks, performance, and targets stay consistent across every disclosure channel.",
    content: [
      { type: "p", text: "The environment for corporate disclosure keeps shifting, and it will continue to. What does not change is the underlying need: companies still have to explain their strategy, risks, performance, and targets clearly — and say the same thing across every channel that carries them." },
      { type: "h2", text: "More scrutiny, not less reporting" },
      { type: "p", text: "Companies still face reporting expectations from investors, customers, lenders, and global jurisdictions — often at the same time. The result is a more fragmented and complicated reporting landscape, not a smaller one. The organizations that navigate it best treat reporting as a strategic discipline rather than an annual obligation." },
      { type: "p", text: "Recent developments make the point — heightened regulatory attention, new climate-reporting requirements at the state and jurisdictional level, and continued adoption of global standards among them. The specifics will keep moving. The need for a consistent, well-grounded story will not." },
      { type: "h2", text: "From promotional to material" },
      { type: "p", text: "The broad, promotional positioning that defined a lot of corporate communication a few years ago is losing its appeal. Companies increasingly want communications grounded in material risk, business strategy, operational performance, governance, and measurable, financially relevant targets — information a serious reader can rely on." },
      { type: "quote", text: "Requirements and frameworks will keep evolving. Your strategy, risks, performance, and financial narrative still have to stay consistent across every disclosure." },
      { type: "h2", text: "One story, consistently told" },
      { type: "p", text: "The real risk today is rarely design. It is disconnection — when the annual report, the sustainability report, and investor materials tell different stories, or emphasize different priorities. Aligning them into a single coherent narrative is now a genuine competitive advantage, and a mark of a well-run reporting program." },
      { type: "h2", text: "What this means for reporting teams" },
      { type: "p", text: "Ground the narrative in what is material. Connect performance and strategy across financial and sustainability disclosures. Make targets specific and substantiated. And build one reporting architecture rather than several disconnected exercises — so every disclosure reinforces the same clear, credible story, with less internal effort to produce it." },
    ],
  },
  {
    slug: "sustainability-reporting-beyond-design",
    title: "Strong Sustainability Reporting Beyond Design",
    category: "Sustainability",
    date: "June 2026",
    readTime: "6 min read",
    image: REPORTS.primaryEnergy,
    metaDescription:
      "A beautiful sustainability report isn’t the goal—clarity, credibility and decision-useful information are. What makes reporting earn trust, beyond the design.",
    excerpt:
      "A beautiful sustainability report is not the goal. Clarity, credibility, and decision-useful information are. Here is what separates a report that earns trust from one that simply looks the part.",
    content: [
      { type: "p", text: "It is tempting to treat a sustainability report primarily as a design project. The strongest reporting programs bring strategy, materiality, content, structure, and design together from the start — grounded in a clear understanding of who is reading, what matters to them, and what they need to understand." },
      { type: "h2", text: "Start with the decision, not the data" },
      { type: "p", text: "Investors, regulators, employees, and communities do not read sustainability reports for entertainment. They read them to make decisions: whether to invest, whether to partner, whether to trust. Strong reporting works backward from those decisions, surfacing the information each audience needs in a structure they can navigate quickly." },
      { type: "quote", text: "The best reports do not just disclose. They help the reader understand performance, risk, and impact — and act on it with confidence." },
      { type: "h2", text: "Credibility is built, not styled" },
      { type: "p", text: "Different frameworks and standards — from the EU’s revised ESRS to the ISSB’s IFRS S1 and S2, GRI, and SASB — are designed to answer different questions and serve different audiences. They can provide structure and comparability, but they do not replace judgment. Credibility comes from consistent data, evidence-backed claims, a candid account of progress and gaps, and a clear connection between sustainability priorities and business strategy. Design should make that credibility easier to see, navigate, and understand." },
      { type: "list", items: [
        "Define your audiences and the decisions they are making",
        "Anchor the narrative in material issues, not everything at once",
        "Connect commitments to measurable progress and business strategy",
        "Let structure and hierarchy do the heavy lifting before visuals do",
      ] },
      { type: "p", text: "When strategy, content, and design are developed together — with senior people involved at every stage — the result is a report that is not only good-looking, but genuinely useful. That is the difference between a document that sits on a shelf and one that moves a conversation forward." },
    ],
  },
  {
    slug: "annual-reports-investors-actually-read",
    title: "Making Annual Reports Investors Actually Read",
    category: "Reporting",
    date: "May 2026",
    readTime: "5 min read",
    image: REPORTS.kennedyWilson,
    metaDescription:
      "Annual reports carry required content; the best are built to be understood—connecting strategy, performance and context into a story investors can actually read.",
    excerpt:
      "Annual reports carry required content. The strongest are also built to be understood — connecting strategy, performance, and context in a narrative investors can navigate quickly.",
    content: [
      { type: "p", text: "Annual reports carry a significant disclosure burden. The strongest use narrative, structure, and design to help investors understand what matters, how the business performed, and where management is taking it next. The challenge is rarely the numbers themselves. It is making the story around them clear, coherent, and easy to navigate." },
      { type: "h2", text: "Lead with the story, support with the data" },
      { type: "p", text: "Investors are trying to answer a simple question: is this a business worth backing over the long term? A strong annual report answers that question early and confidently, then uses financial and operational detail to substantiate it. When the narrative and the data reinforce one another, the whole report becomes more persuasive." },
      { type: "quote", text: "Clarity is a competitive advantage. When your story is easy to follow, your business is easier to trust." },
      { type: "h2", text: "Design for the way people actually read" },
      { type: "p", text: "Few readers move linearly through 80 pages. They scan, jump, and return. Clear hierarchy, meaningful section openers, well-chosen data visualization, and consistent typography let readers find what they need and absorb the message even at a glance." },
      { type: "list", items: [
        "Open with a confident, plain-language equity story",
        "Use hierarchy so key messages survive a fast scan",
        "Turn complex financials into clear, honest visuals",
        "Keep the voice consistent from the letter to the footnotes",
      ] },
      { type: "p", text: "None of this means cutting corners on rigor. It means bringing the same rigor to communication that finance teams bring to the numbers — so the report earns the attention it deserves." },
    ],
  },
  {
    slug: "interactive-reports-for-real-engagement",
    title: "Designing Interactive Reports for Real Engagement",
    category: "Digital",
    date: "April 2026",
    readTime: "5 min read",
    image: REPORTS.meritage,
    metaDescription:
      "An interactive report is more than a PDF on a webpage. Done well, it lets stakeholders explore what matters to them—and shows you what they care about most.",
    excerpt:
      "An interactive report is more than a PDF on a webpage. Done well, it lets stakeholders explore what matters to them — and gives you a clearer picture of what they care about.",
    content: [
      { type: "p", text: "Interactive reporting has matured well past the point of posting a PDF online. The best report websites let each stakeholder follow their own path — an investor drilling into performance, an employee exploring culture and impact, a community member checking local commitments — all from the same source of truth." },
      { type: "h2", text: "Design around exploration, not decoration" },
      { type: "p", text: "Motion and interactivity should serve comprehension, not distract from it. Smart navigation, progressive disclosure, and accessible data visualization make dense information easier to explore. Every interaction should help the reader understand something faster than static pages could." },
      { type: "quote", text: "Interactivity earns its place when it makes complex information easier to understand — not just more impressive to look at." },
      { type: "h2", text: "Accessible and responsive by default" },
      { type: "p", text: "A report that only works on a large screen, or that excludes readers using assistive technology, is not finished. Semantic structure, keyboard navigation, and strong contrast are not add-ons — they are part of doing the work properly, and they widen the audience that can engage with your story." },
      { type: "list", items: [
        "Let each audience follow a path that fits their questions",
        "Use motion to aid understanding, not to show off",
        "Build accessible, responsive experiences from the start",
        "Connect the interactive report to the rest of your communications system",
      ] },
      { type: "p", text: "When an interactive report is built with the same strategy and craft as its print counterpart, it becomes a living part of how an organization communicates — not a one-off microsite that goes stale the day after launch." },
    ],
  },
  {
    slug: "compliance-reporting-that-builds-confidence",
    title: "Turning Compliance Reporting Into a Confidence Builder",
    category: "Compliance",
    date: "March 2026",
    readTime: "6 min read",
    image: REPORTS.primaryEnergy,
    metaDescription:
      "Companies juggle regulatory rules, investor standards and voluntary frameworks. How to make complex disclosure clear and useful—without diluting its rigor.",
    excerpt:
      "Organizations increasingly navigate a mix of regulatory requirements, investor-focused standards, and voluntary reporting frameworks. The communications opportunity is to make complex disclosure clear, traceable, and useful without diluting its rigor.",
    content: [
      { type: "p", text: "Disclosure expectations continue to evolve, and organizations may be working across regulatory requirements, investor-focused standards, and voluntary frameworks at the same time. These are not interchangeable. They serve different purposes, audiences, and information needs. The communications challenge is to present required and expected information accurately, consistently, and in a way stakeholders can understand." },
      { type: "h2", text: "Rigor is the foundation, not the finish line" },
      { type: "p", text: "Accuracy and consistency come first. Data should reconcile, claims should be supported by evidence, and terminology should be applied consistently across the communication. Once that foundation is in place, structure, context, narrative, and design can make complex information easier to navigate and understand." },
      { type: "quote", text: "Clear communication does not dilute rigor. It makes rigor visible." },
      { type: "h2", text: "Make complex disclosure usable" },
      { type: "p", text: "Required and technical disclosure can become difficult to navigate when information is presented primarily around the requirement rather than the reader. Clear structure, plain-language context, thoughtful hierarchy, and well-designed tables can help stakeholders find and understand complex information without oversimplifying it." },
      { type: "list", items: [
        "Distinguish regulatory requirements from voluntary framework guidance",
        "Define the audiences and questions the communication needs to address",
        "Reconcile data and claims before narrative and design development",
        "Use structure, hierarchy, and plain-language context to make dense information easier to navigate",
      ] },
      { type: "p", text: "Handled thoughtfully, disclosure becomes more than an obligation. It can demonstrate discipline, transparency, and respect for stakeholder information needs — strengthening the credibility of the broader corporate story." },
    ],
  },
  {
    slug: "investor-communications-that-earn-conviction",
    title: "Investor Communications That Earn Conviction",
    category: "Investor",
    date: "February 2026",
    readTime: "6 min read",
    image: REPORTS.pdfBlackberryInvestor,
    metaDescription:
      "Investors aren’t short on information—they’re short on time and conviction. Make your equity story easy to grasp, easy to trust and easy to repeat.",
    excerpt:
      "Investors are not short on information — they are short on time and conviction. The strongest investor communications make the equity story easy to grasp, easy to trust, and easy to repeat.",
    content: [
      { type: "p", text: "Every management team believes it has a compelling story. Far fewer communicate it in a way that a busy investor can absorb in minutes and repeat accurately to a colleague. In competitive capital markets, clarity is not a nicety — it is an important part of how investor understanding and conviction are built." },
      { type: "h2", text: "Lead with a clear equity story" },
      { type: "p", text: "The best investor materials open with a plain-language thesis: what the business does, why it wins, and how it creates value over time. Everything that follows — strategy, financials, capital allocation, sustainability — should ladder back to that thesis rather than compete with it. When the story is coherent, each proof point reinforces the last." },
      { type: "quote", text: "Investors back stories they can retell. If your thesis is easy to repeat, it travels — through IR calls, analyst notes, and investment committees." },
      { type: "h2", text: "Be candid about risk and progress" },
      { type: "p", text: "Credibility grows when a company is honest about what is working, what is not, and what it is doing about it. Selective optimism erodes trust; balanced, evidence-backed disclosure earns it. Investors reward management teams that demonstrate they see the business clearly." },
      { type: "list", items: [
        "Open with a plain-language equity thesis, not a data dump",
        "Connect strategy, financials, and capital allocation into one story",
        "Address risks and setbacks directly, with a credible plan",
        "Keep messaging consistent across the deck, report, and IR conversations",
      ] },
      { type: "p", text: "Investor communications are not a quarterly obligation — they are an ongoing relationship. Companies that communicate with clarity and candor build a base of shareholders who understand the long-term story and stay through the noise." },
    ],
  },
];


// Industry perspectives shown alongside our own articles in the combined
// "Insights & Industry Perspectives" grid. These are third-party items
// (LinkedIn posts, articles from other sources) shown as link cards with a
// source pill (e.g. "LinkedIn") next to the topic pill.
//
// To add one: fill `source` (e.g. "LinkedIn"), `category` (topic pill,
// e.g. "Sustainability"), `title`, `excerpt`, `url`, and optional `author`/`date`.
// Items left blank render a subtle placeholder slot (safe to leave while gathering links).
export const PERSPECTIVES = [
  {
    source: "Eco-Business",
    author: "Robin Hodess · GRI",
    category: "Compliance",
    date: "2026-08-04",
    readTime: "8 minute read",
    title: "’Companies are not going to be reporting less’: GRI chief on the future of disclosure",
    excerpt: "GRI CEO Robin Hodess argues GRI and the ISSB are complementary rather than competing — as mandatory reporting expands, companies won’t report less, and the future lies in alignment, interoperability, and credible, decision-useful data.",
    url: "https://www.eco-business.com/news/companies-are-not-going-to-be-reporting-less-gri-chief-on-the-future-of-sustainability-disclosure/",
  },
  {
    source: "LinkedIn",
    author: "Center for Sustainability & Excellence",
    category: "Sustainability",
    addedDate: "2026-08-31",
    title: "Why Sustainability Professionals Are Strategic",
    excerpt: "A case for treating sustainability professionals as strategic value-drivers — connecting material risks, resilience, and long-term performance to core business strategy rather than a compliance afterthought.",
    url: "https://www.linkedin.com/pulse/why-sustainability-professionals-strategic-gwtdf",
  },
  {
    source: "LinkedIn",
    author: "IFRS Foundation",
    category: "Compliance",
    date: "2026-08-18",
    title: "IFRS Foundation strengthens the IASB & ISSB for global markets",
    excerpt: "Trustees approved a five-year operating and financing plan for the IASB and ISSB, confirmed a new ISSB office opening in Geneva in 2027, and proposed targeted amendments to the Foundation’s Constitution (open for comment until 16 Nov 2026).",
    embedUrl: "https://www.linkedin.com/embed/feed/update/urn:li:activity:7495420345773015040",
    url: "https://www.linkedin.com/posts/ifrs-foundation_ifrs-iasb-issb-activity-7495420345773015040-T3fQ",
  },
  {
    source: "LinkedIn",
    author: "Bruno Sarda · EY",
    category: "Sustainability",
    date: "2026-08-10",
    title: "From Extractivism to Regeneration",
    excerpt: "EY’s Bruno Sarda argues the barrier for US business leaders isn’t a lack of ambition — it’s economic systems and incentives built for short-term returns. A case for regenerative business models and systemic change over retrofitted sustainability.",
    url: "https://www.linkedin.com/pulse/from-extractivism-regeneration-what-us-business-leaders-bruno-sarda-9qwhe",
  },
  {
    source: "LinkedIn",
    category: "Sustainability",
    addedDate: "2026-08-31",
    title: "The Future of Climate & Sustainability Leadership: Turning Strategy Into Impact",
    excerpt: "The sustainability leadership mandate is shifting from ambition and storytelling to operational execution — embedding disclosure-grade data, five-year milestones, and cross-functional governance into core business strategy.",
    url: "https://www.linkedin.com/pulse/future-climate-sustainability-leadership-turning-strategy-i96ac",
  },
  {
    source: "LinkedIn",
    category: "Investor",
    addedDate: "2026-08-31",
    title: "IR Impact News",
    excerpt: "A LinkedIn newsletter covering investor relations, corporate reporting, governance, and disclosure trends — practical intelligence for IR and communications teams.",
    url: "https://www.linkedin.com/newsletters/ir-impact-news-7367243705444884483",
  },
  {
    source: "LinkedIn",
    author: "Green Sports Alliance",
    category: "Green Sports",
    date: "2026-08-26",
    title: "Green Sports Alliance",
    excerpt: "Sustainability leadership across the sports industry — greening operations from venues to collegiate athletics, plus events and standards for the sector.",
    embedUrl: "https://www.linkedin.com/embed/feed/update/urn:li:activity:7498491989223665664",
    url: "https://www.linkedin.com/posts/green-sports-alliance_join-us-next-week-for-the-redefining-collegiate-activity-7498491989223665664-d2MS",
  },
];